import React, { useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Chip,
  Button, IconButton, Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ScheduleIcon from '@material-ui/icons/Schedule';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { findBestOrder } from '../../utils/consolidationValidator';

const useStyles = makeStyles(() => ({
  content: { minWidth: 620, padding: 0, background: '#f8fafc' },
  banner: {
    display: 'flex', alignItems: 'flex-start', gap: 12,
    padding: '14px 20px',
    background: '#fff',
    borderBottom: '1px solid #e2e8f0',
  },
  bannerAlert: {
    borderLeft: '4px solid #dc2626',
  },
  bannerOk: {
    borderLeft: '4px solid #16a34a',
    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
  },
  otCard: {
    background: '#fff',
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    margin: '10px 16px',
    overflow: 'hidden',
    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
  },
  otHeader: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 14px',
    background: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  otHeaderLate: {
    background: 'linear-gradient(90deg, #fef2f2 0%, #fff 100%)',
    borderBottom: '1px solid #fecaca',
  },
  otNumber: {
    width: 28, height: 28, borderRadius: '50%',
    background: '#1e293b', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.78rem', fontWeight: 800, flexShrink: 0,
  },
  otNumberLate: { background: '#dc2626' },
  stopRow: {
    display: 'grid',
    gridTemplateColumns: '90px 1fr 160px 120px',
    gap: 10,
    padding: '10px 14px',
    alignItems: 'center',
    fontSize: '0.78rem',
    borderBottom: '1px solid #f1f5f9',
  },
  stopRowLast: { borderBottom: 'none' },
  stopRowLate: { background: '#fef2f2' },
  kindChip: {
    height: 20,
    fontSize: '0.62rem',
    fontWeight: 700,
    letterSpacing: '0.03em',
  },
  monoTime: { fontFamily: 'Roboto Mono, monospace', fontSize: '0.78rem' },
  windowSpan: { fontSize: '0.66rem', color: '#64748b', lineHeight: 1.3 },
  summaryBar: {
    display: 'flex', justifyContent: 'space-around',
    padding: '12px 20px',
    background: '#fff',
    borderTop: '1px solid #e2e8f0',
  },
  summaryItem: {
    textAlign: 'center',
  },
}));

function fmtTime(date) {
  if (!date) return '—';
  return new Date(date).toLocaleString('pt-PT', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

export default function ConsolidationConflictsDialog({
  open, timeline, conflicts, orderedOrders, travelProvider, matrixSource, onCancel, onProceed, onReorganize, lang,
}) {
  const classes = useStyles();
  const isPt = lang === 'pt';
  const usingHERE = matrixSource === 'here';

  // Pre-compute whether a reorder attempt can resolve the conflicts.
  const reorderAttempt = useMemo(() => {
    if (!conflicts || conflicts.length === 0) return null;
    if (!orderedOrders || orderedOrders.length < 2) return null;
    return findBestOrder(orderedOrders, { travelProvider });
  }, [conflicts, orderedOrders, travelProvider]);

  const canReorganize = reorderAttempt && !reorderAttempt.hasConflicts;

  // Group timeline events by OT for a clearer, card-based layout.
  const groupedByOT = useMemo(() => {
    if (!timeline || !orderedOrders) return [];
    const map = new Map();
    for (const ev of timeline) {
      const key = String(ev.otId);
      if (!map.has(key)) map.set(key, { otId: ev.otId, orderNumber: ev.orderNumber, events: [] });
      map.get(key).events.push(ev);
    }
    // Keep the order the user/consolidator decided
    return orderedOrders.map((o, idx) => {
      const key = String(o.id || o._id);
      const g = map.get(key);
      return g ? { ...g, position: idx + 1, order: o } : null;
    }).filter(Boolean);
  }, [timeline, orderedOrders]);

  // Totals for the summary bar
  const totals = useMemo(() => {
    if (!timeline || timeline.length < 2) return null;
    const first = timeline[0]?.time;
    const last = timeline[timeline.length - 1]?.time;
    if (!first || !last) return null;
    const totalMin = Math.round((new Date(last) - new Date(first)) / 60000);
    const hrs = Math.floor(totalMin / 60);
    const mins = totalMin % 60;
    return {
      duration: hrs > 0 ? `${hrs}h${String(mins).padStart(2, '0')}` : `${mins} min`,
      stopCount: timeline.length,
      otCount: groupedByOT.length,
      lateCount: (conflicts || []).length,
    };
  }, [timeline, groupedByOT, conflicts]);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth
      PaperProps={{ style: { borderRadius: 14, overflow: 'hidden' } }}>
      <DialogTitle style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Box style={{
            width: 36, height: 36, borderRadius: 10,
            background: conflicts?.length ? 'linear-gradient(135deg, #fef2f2, #fee2e2)' : 'linear-gradient(135deg, #eff6ff, #dbeafe)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {conflicts?.length
              ? <WarningIcon style={{ fontSize: 20, color: '#dc2626' }} />
              : <ScheduleIcon style={{ fontSize: 20, color: '#2563eb' }} />}
          </Box>
          <Box style={{ flex: 1 }}>
            <Typography style={{ fontSize: '1.02rem', fontWeight: 700, color: '#0f172a' }}>
              {isPt ? 'Simulação da rota conjunta' : 'Consolidated route simulation'}
            </Typography>
            <Typography style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2 }}>
              {isPt
                ? `${groupedByOT.length} ordens · ${timeline?.length || 0} paragens`
                : `${groupedByOT.length} orders · ${timeline?.length || 0} stops`}
              {conflicts?.length > 0 && (
                <span style={{ color: '#dc2626', fontWeight: 700 }}>
                  {' · '}{conflicts.length} {isPt ? 'conflito(s)' : 'conflict(s)'}
                </span>
              )}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onCancel}>
            <CloseIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers className={classes.content}>
        {/* Status banner */}
        <Box className={`${classes.banner} ${conflicts?.length ? classes.bannerAlert : ''}`}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginTop: 2 }}>
            {conflicts?.length
              ? <WarningIcon style={{ fontSize: 18, color: '#dc2626' }} />
              : <CheckCircleIcon style={{ fontSize: 18, color: '#16a34a' }} />}
          </Box>
          <Box>
            <Typography style={{ fontSize: '0.8rem', fontWeight: 700, color: conflicts?.length ? '#991b1b' : '#166534' }}>
              {conflicts?.length
                ? (isPt ? 'Atenção: janelas horárias ultrapassadas' : 'Warning: time windows missed')
                : (isPt ? 'Timeline sem conflitos' : 'Timeline with no conflicts')}
            </Typography>
            <Typography style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 2, lineHeight: 1.45 }}>
              {isPt
                ? (usingHERE
                  ? 'Simulação feita com tempos reais de estrada (HERE), considerando trânsito.'
                  : 'Simulação feita com distâncias em linha recta a 60 km/h (indicador inicial).')
                : (usingHERE
                  ? 'Simulated with real HERE travel times (traffic-aware).'
                  : 'Simulated with straight-line distances at 60 km/h.')}
              {conflicts?.length > 0 && (isPt
                ? ' Há conflito quando a hora prevista de chegada ultrapassa a janela horária combinada com o cliente — em baixo, cada ordem mostra a recolha e a entrega, com a hora prevista (a vermelho quando passa da janela).'
                : ' A conflict means the estimated arrival is later than the customer\'s agreed time window — below, each order shows pickup and delivery, with the estimated time (red when it misses the window).')}
            </Typography>
          </Box>
        </Box>

        {/* Reorganize suggestion */}
        {canReorganize && (
          <Box className={`${classes.banner} ${classes.bannerOk}`}>
            <AutorenewIcon style={{ fontSize: 18, color: '#16a34a', flexShrink: 0, marginTop: 2 }} />
            <Typography style={{ fontSize: '0.78rem', color: '#14532d', fontWeight: 600, flex: 1 }}>
              {isPt
                ? 'Encontrámos uma ordem alternativa que evita todos os conflitos. Carrega em "Reorganizar" para aplicar.'
                : "We found a conflict-free alternative order. Click 'Reorganize' to apply."}
            </Typography>
          </Box>
        )}

        {/* Grouped cards — one per OT */}
        {groupedByOT.map((otGroup, otIdx) => {
          const hasLate = otGroup.events.some(e => e.status === 'late');
          return (
            <Box key={otGroup.otId} className={classes.otCard}>
              <Box className={`${classes.otHeader} ${hasLate ? classes.otHeaderLate : ''}`}>
                <Box className={`${classes.otNumber} ${hasLate ? classes.otNumberLate : ''}`}>
                  {otGroup.position}
                </Box>
                <Box style={{ flex: 1 }}>
                  <Typography style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a' }}>
                    {otGroup.order?.customRef?.trim() || otGroup.orderNumber || `OT #${otGroup.otId}`}
                  </Typography>
                  <Typography style={{ fontSize: '0.66rem', color: '#64748b' }}>
                    {otGroup.order?.pickupName?.split(',')[0] || '—'} → {otGroup.order?.deliveryName?.split(',')[0] || '—'}
                  </Typography>
                </Box>
                {hasLate && (
                  <Chip
                    size="small"
                    icon={<WarningIcon style={{ fontSize: 14, color: '#fff' }} />}
                    label={isPt ? 'Atraso' : 'Late'}
                    style={{
                      height: 22, background: '#dc2626', color: '#fff', fontSize: '0.6rem', fontWeight: 700,
                    }}
                  />
                )}
              </Box>
              {otGroup.events.map((ev, evIdx) => {
                const isLate = ev.status === 'late';
                const isEarly = ev.status === 'early';
                const isLast = evIdx === otGroup.events.length - 1;
                return (
                  <Box key={`${ev.otId}-${ev.kind}-${evIdx}`}
                    className={`${classes.stopRow} ${isLate ? classes.stopRowLate : ''} ${isLast ? classes.stopRowLast : ''}`}>
                    <Chip
                      className={classes.kindChip}
                      size="small"
                      icon={ev.kind === 'pickup'
                        ? <span style={{ fontSize: 11, marginLeft: 5 }}>▲</span>
                        : <span style={{ fontSize: 11, marginLeft: 5 }}>▼</span>}
                      label={ev.kind === 'pickup' ? (isPt ? 'Recolha' : 'Pickup') : (isPt ? 'Entrega' : 'Delivery')}
                      style={{
                        background: ev.kind === 'pickup' ? '#dcfce7' : '#fee2e2',
                        color: ev.kind === 'pickup' ? '#166534' : '#991b1b',
                      }}
                    />
                    <Typography style={{ fontSize: '0.72rem', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {ev.kind === 'pickup'
                        ? (otGroup.order?.pickupName || otGroup.order?.pickupAddress || '—')
                        : (otGroup.order?.deliveryName || otGroup.order?.deliveryAddress || '—')}
                    </Typography>
                    <Typography className={classes.windowSpan}>
                      {ev.windowFrom
                        ? <>
                            <span style={{ color: '#94a3b8' }}>{isPt ? 'Janela: ' : 'Window: '}</span>
                            {fmtTime(ev.windowFrom)}{ev.windowTo ? ` – ${fmtTime(ev.windowTo).split(' ')[1] || ''}` : ''}
                          </>
                        : (isPt ? 'Sem janela' : 'No window')}
                    </Typography>
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                      {isLate && <WarningIcon style={{ fontSize: 14, color: '#dc2626' }} />}
                      {isEarly && <AccessTimeIcon style={{ fontSize: 14, color: '#eab308' }} />}
                      {ev.status === 'ok' && <CheckCircleIcon style={{ fontSize: 14, color: '#22c55e' }} />}
                      <Typography className={classes.monoTime} style={{
                        color: isLate ? '#dc2626' : isEarly ? '#a16207' : '#0f172a',
                        fontWeight: isLate ? 700 : 600,
                      }}>
                        {fmtTime(ev.time)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          );
        })}

        {/* Summary totals */}
        {totals && (
          <Box className={classes.summaryBar}>
            <Box className={classes.summaryItem}>
              <Typography style={{ fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {isPt ? 'Ordens' : 'Orders'}
              </Typography>
              <Typography style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a' }}>
                {totals.otCount}
              </Typography>
            </Box>
            <Box className={classes.summaryItem}>
              <Typography style={{ fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {isPt ? 'Paragens' : 'Stops'}
              </Typography>
              <Typography style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a' }}>
                {totals.stopCount}
              </Typography>
            </Box>
            <Box className={classes.summaryItem}>
              <Typography style={{ fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {isPt ? 'Duração estimada' : 'Estimated time'}
              </Typography>
              <Typography style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a' }}>
                {totals.duration}
              </Typography>
            </Box>
            {totals.lateCount > 0 && (
              <Box className={classes.summaryItem}>
                <Typography style={{ fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {isPt ? 'Conflitos' : 'Conflicts'}
                </Typography>
                <Typography style={{ fontSize: '1.1rem', fontWeight: 800, color: '#dc2626' }}>
                  {totals.lateCount}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Guia das opções — elucida o operador sobre o que cada botão faz. */}
        {conflicts?.length > 0 && (
          <Box style={{ marginTop: 8, padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <Typography style={{ fontSize: '0.67rem', color: '#475569', lineHeight: 1.55 }}>
              {isPt ? (<>
                <b>O que podes fazer:</b><br />
                • <b style={{ color: '#16a34a' }}>Reorganizar automaticamente</b> — aplica a melhor ordem das paragens que evita os atrasos{!canReorganize ? ' (indisponível: nenhuma ordem consegue cumprir todas as janelas)' : ''}.<br />
                • <b style={{ color: '#dc2626' }}>Criar rota mesmo assim</b> — mantém a ordem atual e cria a rota, aceitando os atrasos (o motorista vê-a na mesma).<br />
                • <b>Cancelar</b> — não cria a rota; podes ajustar as janelas horárias das OTs ou a seleção e tentar de novo.
              </>) : (<>
                <b>What you can do:</b><br />
                • <b style={{ color: '#16a34a' }}>Auto-reorganize</b> — applies the best stop order that avoids the delays{!canReorganize ? ' (unavailable: no order can meet all windows)' : ''}.<br />
                • <b style={{ color: '#dc2626' }}>Create anyway</b> — keeps the current order and creates the route, accepting the delays.<br />
                • <b>Cancel</b> — does not create the route; adjust the OT time windows or selection and try again.
              </>)}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions style={{ padding: '12px 20px', background: '#fff' }}>
        <Button onClick={onCancel} style={{ textTransform: 'none', color: '#64748b' }}>
          {isPt ? 'Cancelar' : 'Cancel'}
        </Button>
        <Box style={{ flex: 1 }} />
        {canReorganize && (
          <Tooltip title={isPt ? 'Aplicar ordem alternativa sem conflitos' : 'Apply conflict-free ordering'}>
            <Button
              variant="outlined"
              onClick={() => onReorganize(reorderAttempt.reorderedOrders)}
              startIcon={<AutorenewIcon style={{ fontSize: 16 }} />}
              style={{ textTransform: 'none', fontWeight: 600, color: '#16a34a', borderColor: '#16a34a' }}
            >
              {isPt ? 'Reorganizar automaticamente' : 'Auto-reorganize'}
            </Button>
          </Tooltip>
        )}
        <Button
          variant="contained"
          onClick={onProceed}
          startIcon={conflicts?.length
            ? <WarningIcon style={{ fontSize: 16 }} />
            : <CheckCircleIcon style={{ fontSize: 16 }} />}
          style={{
            textTransform: 'none', fontWeight: 700,
            background: conflicts?.length
              ? 'linear-gradient(135deg, #dc2626, #b91c1c)'
              : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
            color: '#fff',
            boxShadow: '0 2px 6px rgba(15,23,42,0.1)',
          }}
        >
          {isPt
            ? (conflicts?.length ? 'Criar rota mesmo assim' : 'Criar rota')
            : (conflicts?.length ? 'Create anyway' : 'Create route')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
